import React from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import {
  forgetImageSource,
  isImageSourceLoaded,
  isImageSourceRetained,
  markImageSourceLoaded,
  resetImageSourceCache,
  subscribeToImageSource,
} from "../../services/portalAluno/images/imageCache";
import "./styles.css";

type FastImageStatus = "loading" | "loaded" | "error";

interface FastImageProps {
  src?: string;
  alt: string;
  className?: string;
  width?: number;
  height?: number;
  sizes?: string;
  srcSet?: string;
  priority?: boolean;
  draggable?: boolean;
  skeleton?: boolean;
  fallback?: React.ReactNode;
}

const skeletonColors = {
  baseColor: "var(--portal-skeleton-base)",
  highlightColor: "var(--portal-skeleton-highlight)",
};

export const isFastImageLoaded = (src?: string) =>
  isImageSourceLoaded(src);

export const resetFastImageCache = () => {
  resetImageSourceCache();
};

const getInitialStatus = (src?: string): FastImageStatus =>
  isFastImageLoaded(src) ? "loaded" : "loading";

const getSettledStatus = (
  node: HTMLImageElement | null
): FastImageStatus | null => {
  if (!node || !node.complete || !node.currentSrc) return null;

  return node.naturalWidth > 0 ? "loaded" : "error";
};

const FastImage = ({
  src,
  alt,
  className = "",
  width,
  height,
  sizes,
  srcSet,
  priority = false,
  draggable = false,
  skeleton = true,
  fallback,
}: FastImageProps) => {
  const imageRef = React.useRef<HTMLImageElement | null>(null);
  const [status, setStatus] = React.useState<FastImageStatus>(() =>
    getInitialStatus(src)
  );

  React.useEffect(() => {
    const node = imageRef.current;

    if (!src || !node) return undefined;

    const settle = (next: FastImageStatus) => {
      if (next === "loaded") markImageSourceLoaded(src, node);
      else forgetImageSource(src);

      setStatus(next);
    };

    const handleLoad = () => settle("loaded");
    const handleError = () => settle("error");
    const unsubscribeFromCache = subscribeToImageSource(src, setStatus);

    node.addEventListener("load", handleLoad);
    node.addEventListener("error", handleError);

    const settled = getSettledStatus(node);

    if (settled) settle(settled);
    else setStatus(getInitialStatus(src));

    return () => {
      node.removeEventListener("load", handleLoad);
      node.removeEventListener("error", handleError);
      unsubscribeFromCache();
    };
  }, [src]);

  const wrapperClassName = `fast-image fast-image--${status}${
    className ? ` ${className}` : ""
  }`;
  const hasDecodedImage = isImageSourceRetained(src);
  const shouldLoadEagerly = priority || hasDecodedImage;

  const priorityAttribute = {
    fetchpriority: priority ? "high" : "auto",
  } as React.ImgHTMLAttributes<HTMLImageElement>;

  if (!src || status === "error") {
    return (
      <span className={wrapperClassName} role="img" aria-label={alt}>
        {fallback}
      </span>
    );
  }

  return (
    <span className={wrapperClassName}>
      {status === "loading" && skeleton && (
        <span className="fast-image__skeleton" aria-hidden="true">
          <Skeleton
            {...skeletonColors}
            borderRadius={0}
            height="100%"
            width="100%"
          />
        </span>
      )}

      <img
        ref={imageRef}
        className="fast-image__img"
        src={src}
        alt={alt}
        width={width}
        height={height}
        sizes={sizes}
        srcSet={srcSet}
        draggable={draggable}
        decoding={hasDecodedImage ? "sync" : "async"}
        loading={shouldLoadEagerly ? "eager" : "lazy"}
        {...priorityAttribute}
      />
    </span>
  );
};

export default React.memo(FastImage);
